import { useEffect, useState } from "react";
import { API_URL, fmtDateTime, fmtPhone } from "./lib/api";

/**
 * "Call Summary" — at-a-glance view of a call. Stays compact: a meta KV
 * grid, a row of artefact counts, and call notes. The detail tabs
 * (Transcript / Tool calls / Logs) own the rich, drill-in views.
 *
 * "Print full record" triggers `window.print()`. The matching @media
 * print rules in styles/components/receipt.css show the entire detail
 * page (summary + every tab's content), so the printed document is a
 * complete audit artefact.
 */

type CallShape = {
  id: number;
  direction: string;
  from_number: string;
  to_number: string;
  started_at: string;
  ended_at: string | null;
  outcome: string | null;
  recording_url: string | null;
  customer_id: number | null;
  first_name: string | null;
  last_name: string | null;
  customer_phone: string | null;
  /** Telnyx id is only on legs we originated; outbound legs don't surface it. */
  telnyx_call_control_id?: string | null;
  model_snapshot: string | null;
  notes: string | null;
};

type Counts = {
  transcripts: number;
  toolCalls: number;
};

export function CallSummary({
  call,
  callId,
  counts,
}: {
  call: CallShape;
  callId: number;
  counts: Counts;
}) {
  // Advice count is fetched separately — Inbound/Outbound parents don't
  // keep advice in their state, so the summary asks for it directly.
  const [adviceCount, setAdviceCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/calls/${callId}/advice`);
        if (!r.ok) return;
        const d = (await r.json()) as { advice: unknown[] };
        if (!cancelled) setAdviceCount(d.advice?.length ?? 0);
      } catch {
        /* summary still renders without it */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callId]);

  const callerName =
    [call.first_name, call.last_name].filter(Boolean).join(" ") ||
    call.customer_phone ||
    call.from_number;
  const duration = call.ended_at
    ? formatDurationSeconds(
        (parseTs(call.ended_at).getTime() - parseTs(call.started_at).getTime()) / 1000,
      )
    : "ongoing";

  return (
    <article id={`call-summary-${callId}`} className="receipt">
      <button
        id={`call-summary-print-${callId}`}
        type="button"
        className="receipt-print-btn"
        onClick={() => window.print()}
      >
        Print full record
      </button>

      {/* The "Summary" tab label is already the page heading — repeating
          "Call Summary" in an h2 here was redundant. The print button is
          absolute-positioned so it stays in the top-right corner without
          a header row. */}

      <section id={`call-summary-meta-${callId}`} className="receipt-meta">
        <ReceiptKV label="Caller" value={callerName} />
        <ReceiptKV label="Direction" value={call.direction} />
        <ReceiptKV
          label="From → To"
          value={`${fmtPhone(call.from_number)} → ${fmtPhone(call.to_number)}`}
        />
        <ReceiptKV label="Started" value={fmtDateTime(call.started_at)} />
        <ReceiptKV
          label="Ended"
          value={call.ended_at ? fmtDateTime(call.ended_at) : "—"}
        />
        <ReceiptKV label="Duration" value={duration} />
        <ReceiptKV label="Outcome" value={call.outcome || "—"} />
        {call.telnyx_call_control_id && (
          <ReceiptKV
            label="Telnyx ID"
            value={call.telnyx_call_control_id}
            mono
          />
        )}
      </section>

      {/* Compact stats strip — one row of counts that link to the
          dedicated tabs for the operator who wants to drill in. */}
      <section id={`call-summary-stats-${callId}`} className="summary-stats">
        <SummaryStat
          label="Turns"
          value={counts.transcripts}
          hint="Open the Transcript tab for the full back-and-forth."
        />
        <SummaryStat
          label="Tool calls"
          value={counts.toolCalls}
          hint="Open the Tool calls tab for arguments and results."
        />
        <SummaryStat
          label="Advisor cards"
          value={adviceCount ?? "—"}
          hint="Live agent guidance saved during the call."
        />
      </section>

      {call.notes && (
        <section id={`call-summary-notes-${callId}`} className="receipt-section">
          <h3>Notes</h3>
          <p>{call.notes}</p>
        </section>
      )}

      <footer id={`call-summary-foot-${callId}`} className="receipt-foot">
        Generated {fmtDateTime(new Date().toISOString())} · Agent Copilot
      </footer>
    </article>
  );
}

function ReceiptKV({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="receipt-kv">
      <span className="receipt-kv-label">{label}</span>
      <span className={`receipt-kv-value${mono ? " is-mono" : ""}`}>{value}</span>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint: string;
}) {
  return (
    <div className="summary-stat" title={hint}>
      <span className="summary-stat-value">{value}</span>
      <span className="summary-stat-label">{label}</span>
    </div>
  );
}

// Duplicate of lib/api parseTs because the receipt is otherwise self-contained.
function parseTs(ts: string): Date {
  const hasTz = ts.endsWith("Z") || /[+-]\d\d:?\d\d$/.test(ts);
  const iso = hasTz ? ts : `${ts.replace(" ", "T")}Z`;
  return new Date(iso);
}

function formatDurationSeconds(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return "—";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
